/**
 * User display preferences for formatting dates, times, currency, and measurements.
 * These preferences only affect how data is displayed, not how it's stored.
 */

export type TimeFormat = '12h' | '24h';
export type DateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
export type UnitSystem = 'imperial' | 'metric';
export type WeekStart = 'sunday' | 'monday';

/**
 * Common currency codes supported by the app.
 * Uses ISO 4217 currency codes.
 */
export const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
] as const;

export type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number]['code'];

/**
 * User display preferences stored in Firestore.
 */
export interface UserDisplayPreferences {
  /** Time display format: 12-hour (1:30 PM) or 24-hour (13:30) */
  timeFormat: TimeFormat;
  /** Date display format */
  dateFormat: DateFormat;
  /** Currency code for displaying monetary values (ISO 4217) */
  currency: string;
  /** Unit system for measurements (imperial: ft, lb / metric: m, kg) */
  units: UnitSystem;
  /** First day of the week in calendar views */
  weekStartsOn: WeekStart;
}

/**
 * Default preferences used when user has no saved preferences.
 */
export const DEFAULT_DISPLAY_PREFERENCES: UserDisplayPreferences = {
  timeFormat: '12h',
  dateFormat: 'MM/DD/YYYY',
  currency: 'USD',
  units: 'imperial',
  weekStartsOn: 'sunday',
};

/**
 * Date format options for the preferences UI.
 */
export const DATE_FORMAT_OPTIONS: { value: DateFormat; label: string; example: string }[] = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '01/24/2026' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '24/01/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-01-24' },
];

/**
 * Time format options for the preferences UI.
 */
export const TIME_FORMAT_OPTIONS: { value: TimeFormat; label: string; example: string }[] = [
  { value: '12h', label: '12-hour', example: '2:30 PM' },
  { value: '24h', label: '24-hour', example: '14:30' },
];

/**
 * Unit system options for the preferences UI.
 */
export const UNIT_SYSTEM_OPTIONS: { value: UnitSystem; label: string }[] = [
  { value: 'imperial', label: 'Imperial (ft, lb)' },
  { value: 'metric', label: 'Metric (m, kg)' },
];

/**
 * Week start options for the preferences UI.
 */
export const WEEK_START_OPTIONS: { value: WeekStart; label: string }[] = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
];
